import { ChartData, ChartDataset } from 'chart.js';
import { eachDayOfInterval, format, isWeekend } from 'date-fns';

import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsProvider';
import { useGetAllTeams } from '../../../hooks/useGetAllTeams';
import { calculatePercentage } from '../../../utils';
import { calculateDateRanges } from '../../../utils/dateUtils';
import { teamProperties } from '../constants';

import { useGetTeamWiseUserCount } from './useGetTeamWiseUserCount';
import { useTeamWiseCheckinsData } from './useTeamWiseCheckinsData';

export function useOverallChartData(): ChartData<'line', number[], string> {
  const { selectedPeriod } = useAnalytics();
  const dateRange = calculateDateRanges(selectedPeriod.value);
  const { teamWiseUserCount } = useGetTeamWiseUserCount();
  const { teamWiseCheckinsData, teamWiseLeavesData } = useTeamWiseCheckinsData(
    dateRange.thisPeriodStart,
    dateRange.thisPeriodEnd,
  );
  const { data: teams } = useGetAllTeams();
  const labels = eachDayOfInterval({
    start: new Date(dateRange.thisPeriodStart),
    end: new Date(dateRange.thisPeriodEnd),
  })
    .filter((date) => !isWeekend(date))
    .map((date) => format(date, 'yyyy-MM-dd'));

  const datasets: ChartDataset<'line', number[]>[] = Object.keys(
    teamWiseUserCount,
  )
    .filter(
      (team): team is keyof typeof teamProperties =>
        team !== null && team in teamProperties,
    )
    .map((team) => {
      const teamProperty = teamProperties[team];
      const teamId = teams?.find((t) => t.name === team)?.id;
      return {
        label: team,
        id: teamId,
        data: labels.map((date) => {
          const userCount = teamWiseUserCount[team];
          const leavesCount = teamWiseLeavesData[team]?.[date] || 0;
          const checkInsCount = teamWiseCheckinsData[team]?.[date] || 0;
          const totalSessions = (userCount - leavesCount) * 2;
          return calculatePercentage(
            Math.min(totalSessions, checkInsCount),
            totalSessions,
          );
        }),
        fill: '+1',
        borderWidth: 2,
        backgroundColor: 'transparent',
        borderColor: teamProperty?.borderColor || 'black',
        tension: 0.1,
        pointBackgroundColor: 'white',
      };
    });

  return {
    labels,
    datasets,
  };
}
